import { Code2, Copy, Link2, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetPublicEventShareLink } from '~entities/events/api/get-public-event-share-link';
import { useRotatePublicEventShareLink } from '~entities/events/api/rotate-public-event-share-link';
import type { EventDetails } from '~entities/events/modal/event';
import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { copyToClipboard } from '~shared/utils/clipboard';

import {
  buildEventPublicShareEmbedUrl,
  buildEventPublicShareIframeEmbedHtml,
  buildEventPublicShareUrl,
} from './build-event-public-share-url';

interface EventPublicShareLinkSectionProps {
  eventId: string;
  eventData: EventDetails | undefined;
  /**
   * Current studio context. `locationId` (GHL) is only embedded when this
   * studio owns the event (`eventData.studio === currentStudio._id`).
   */
  currentStudio:
    | {
        _id?: string;
        locationId?: string;
      }
    | undefined;
}

export function EventPublicShareLinkSection({
  eventId,
  eventData,
  currentStudio,
}: EventPublicShareLinkSectionProps) {
  const mutation = useRotatePublicEventShareLink(eventId);
  const shareLinkQuery = useGetPublicEventShareLink(eventId);
  const [knownShareToken, setKnownShareToken] = useState('');

  useEffect(() => {
    setKnownShareToken('');
  }, [eventId]);

  const eventShareToken =
    typeof eventData?.publicEventShareToken === 'string'
      ? eventData.publicEventShareToken.trim()
      : '';
  const fetchedShareToken =
    typeof shareLinkQuery.data?.shareToken === 'string'
      ? shareLinkQuery.data.shareToken.trim()
      : '';
  const mutationShareToken =
    typeof mutation.data?.shareToken === 'string'
      ? mutation.data.shareToken.trim()
      : '';
  const shareToken =
    knownShareToken ||
    mutationShareToken ||
    fetchedShareToken ||
    eventShareToken;

  const portalBase = import.meta.env.VITE_PARENT_PORTAL_URL;
  const base =
    typeof portalBase === 'string' && portalBase.trim().length > 0
      ? portalBase.replace(/\/$/, '')
      : '';

  const eventStudioMongoId =
    typeof eventData?.studio === 'string' ? eventData.studio.trim() : '';
  const currentStudioMongoId =
    typeof currentStudio?._id === 'string' ? currentStudio._id.trim() : '';
  const currentGhlLocationId =
    typeof currentStudio?.locationId === 'string'
      ? currentStudio.locationId.trim()
      : '';
  const studioParam =
    eventStudioMongoId.length > 0 &&
    currentStudioMongoId.length > 0 &&
    eventStudioMongoId === currentStudioMongoId &&
    currentGhlLocationId.length > 0
      ? currentGhlLocationId
      : '';

  const shareUrl = useMemo(
    () =>
      buildEventPublicShareUrl({
        portalBase: base,
        shareToken,
        studioId: studioParam,
      }),
    [base, shareToken, studioParam],
  );

  const iframeHtml = useMemo(() => {
    const embedUrl = buildEventPublicShareEmbedUrl(shareUrl);
    return buildEventPublicShareIframeEmbedHtml(embedUrl);
  }, [shareUrl]);

  const portalBaseMissing = !base;
  const portalUrlInvalid = Boolean(base) && shareToken.length > 0 && !shareUrl;
  const isResolvingExistingLink =
    shareToken.length === 0 && shareLinkQuery.isLoading;

  const handleCopy = async () => {
    if (!shareUrl) {
      toast.error('Link not ready');
      return;
    }
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      toast.success('Link copied');
    } else {
      toast.error('Could not copy');
    }
  };

  const handleCopyEmbed = async () => {
    if (!iframeHtml) {
      toast.error('Embed code not ready');
      return;
    }
    const ok = await copyToClipboard(iframeHtml);
    if (ok) {
      toast.success('Embed code copied', {
        description: 'Paste it into an HTML or embed block on your website.',
      });
    } else {
      toast.error('Could not copy embed code');
    }
  };

  const handleGenerate = async () => {
    try {
      const result = await mutation.mutateAsync(false);
      const next =
        typeof result?.shareToken === 'string' ? result.shareToken.trim() : '';
      if (next.length === 0) {
        toast.error('Could not create link');
        return;
      }
      setKnownShareToken(next);
      toast.success('Public link created');
    } catch {
      toast.error('Could not create link');
    }
  };

  const handleRegenerate = async () => {
    const ok = window.confirm(
      'Regenerate this link? The previous URL will stop working.',
    );
    if (!ok) return;
    try {
      const result = await mutation.mutateAsync(true);
      const next =
        typeof result?.shareToken === 'string' ? result.shareToken.trim() : '';
      if (next.length === 0) {
        toast.error('Could not regenerate link');
        return;
      }
      setKnownShareToken(next);
      toast.success('New public link is ready');
    } catch {
      toast.error('Could not regenerate link');
    }
  };

  return (
    <div className='space-y-3 rounded-lg border border-border p-4'>
      <div className='flex items-start gap-2'>
        <Link2 className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
        <Label className='text-base'>Unique Public Event Page</Label>
      </div>

      {isResolvingExistingLink ? (
        <p className='text-sm text-muted-foreground'>Loading public link…</p>
      ) : shareToken.length === 0 ? (
        <Button
          type='button'
          variant='secondary'
          disabled={mutation.isPending}
          onClick={() => {
            void handleGenerate();
          }}
        >
          Generate public link
        </Button>
      ) : shareUrl ? (
        <div className='flex gap-2'>
          <Input readOnly value={shareUrl} className='font-mono text-xs' />
          <Button
            type='button'
            variant='secondary'
            size='icon'
            onClick={() => {
              void handleCopy();
            }}
            aria-label='Copy public event link'
            title='Copy public event link'
          >
            <Copy className='size-4' />
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            disabled={!iframeHtml}
            onClick={() => {
              void handleCopyEmbed();
            }}
            aria-label='Copy embed code'
            title='Copy embed code (HTML iframe)'
          >
            <Code2 className='size-4' />
          </Button>
          <Button
            type='button'
            variant='secondary'
            size='icon'
            disabled={mutation.isPending}
            onClick={() => {
              void handleRegenerate();
            }}
            aria-label='Regenerate public event link'
            title='Regenerate public event link'
          >
            <RefreshCw className='size-4' />
          </Button>
        </div>
      ) : (
        <div className='space-y-2'>
          <p className='text-sm text-destructive'>
            {portalBaseMissing
              ? 'Parent Portal URL is not configured; contact your administrator. You can still regenerate the share token.'
              : portalUrlInvalid
                ? 'Parent Portal URL is invalid, so the share link cannot be built. Check VITE_PARENT_PORTAL_URL (including https://).'
                : 'Share link could not be built.'}
          </p>
          <Button
            type='button'
            variant='secondary'
            size='sm'
            disabled={mutation.isPending}
            onClick={() => {
              void handleRegenerate();
            }}
          >
            <RefreshCw className='mr-2 size-4' />
            Regenerate token
          </Button>
        </div>
      )}
    </div>
  );
}
